"use client";

import { AlertTriangle, ArrowRight, RefreshCw } from "lucide-react";
import { HistoryActorAvatar } from "@aera/data-table";
import { AeraButton } from "@aera/ui";
import type { ReceivableStaleConflict } from "@/app/artifacts/table/receivableStale";

export function ReceivableStaleBanner({
  conflict,
  entityLabel,
  isReloading,
  onUseRemote,
}: {
  conflict: ReceivableStaleConflict;
  /** e.g. สลิปค้าง / ยอดค้าง — matches the tab the modal was opened from */
  entityLabel: string;
  isReloading?: boolean;
  onUseRemote: () => void;
}) {
  const { notice, diffs } = conflict;
  const actorLabel = notice.actorName?.trim() || "ผู้ใช้อื่น";
  const isSelf = actorLabel === "คุณ";
  const showActorAvatar = Boolean(notice.actorId?.trim());
  const actionPhrase = notice.actionTitle?.trim()
    ? isSelf
      ? `คุณเพิ่ง「${notice.actionTitle.trim()}」`
      : `${actorLabel} เพิ่ง「${notice.actionTitle.trim()}」`
    : isSelf
      ? "คุณเพิ่งแก้รายการนี้"
      : `${actorLabel} เพิ่งแก้รายการนี้`;

  return (
    <div
      role="alert"
      className="shrink-0 border rounded-xl border-amber-200/90 bg-amber-50 px-4 py-3 mb-4"
    >
      <div className="space-y-3">
        <div className="flex min-w-0 gap-2.5">
          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
            <AlertTriangle className="h-4 w-4" aria-hidden />
          </div>
          <div className="min-w-0 space-y-1.5">
            <p className="text-sm font-bold leading-snug text-amber-950">
              {entityLabel}มีการอัปเดตแล้ว
            </p>
            <div className="flex flex-wrap items-center gap-2 text-xs leading-relaxed text-amber-900/85">
              {showActorAvatar ? (
                <HistoryActorAvatar
                  actorId={notice.actorId}
                  actorLabel={actorLabel}
                />
              ) : null}
              <span>
                {actionPhrase}
                <span className="text-amber-900/55"> · {notice.occurredAt}</span>
              </span>
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-amber-200/80 bg-white">
          <div className="flex items-center justify-between gap-2 border-b border-amber-100 bg-amber-50/50 px-3 py-2">
            <p className="text-[11px] font-bold text-amber-950/70">สิ่งที่เปลี่ยน</p>
            <p className="text-[10px] font-semibold text-amber-900/55">
              ที่ดูอยู่ → ข้อมูลใหม่
            </p>
          </div>
          <ul className="divide-y divide-amber-100">
            {diffs.map((diff) => (
              <li
                key={diff.label}
                className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 px-3 py-2.5"
              >
                <span className="text-xs font-semibold text-aera-lightslate">
                  {diff.label}
                </span>
                <div className="flex min-w-0 items-center gap-1.5 tabular-nums">
                  <span className="text-sm font-medium text-aera-lightslate line-through decoration-aera-lightslate/40">
                    {diff.localValue}
                  </span>
                  <ArrowRight
                    className="h-3.5 w-3.5 shrink-0 text-amber-700/70"
                    aria-hidden
                  />
                  <span className="text-sm font-bold text-aera-charcoal">
                    {diff.remoteValue}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <p className="text-right text-[11px] leading-relaxed text-amber-900/75">
          กดอัปเดตเพื่อใช้ข้อมูลใหม่ของ{entityLabel}
        </p>

        <div className="flex justify-end">
          <AeraButton
            type="button"
            variant="primary"
            size="sm"
            fontWeight="semibold"
            className="cursor-pointer"
            disabled={isReloading}
            onClick={onUseRemote}
            icon={
              <RefreshCw
                className={`h-4 w-4 ${isReloading ? "animate-spin" : ""}`}
                aria-hidden
              />
            }
          >
            {isReloading ? "กำลังอัปเดต..." : "อัปเดตเลย"}
          </AeraButton>
        </div>
      </div>
    </div>
  );
}

export function ReceivableViewingStaleChip({
  actorName,
  entityLabel,
  onUseRemote,
}: {
  actorName: string;
  entityLabel: string;
  onUseRemote: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-200/80 bg-amber-50/70 px-3 py-2">
      <p className="text-xs font-medium text-amber-950">
        กำลังดู{entityLabel}แบบเก่า — {actorName} แก้ไปแล้ว
      </p>
      <button
        type="button"
        onClick={onUseRemote}
        className="cursor-pointer text-xs font-bold text-amber-900 underline-offset-2 hover:underline"
      >
        อัปเดตเลย
      </button>
    </div>
  );
}
